import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { ITrendParams } from 'type/tracking';

import ContentTypeDistribution from 'components/tracking/performance/ContentTypeDistribution';
import PerformanceCards from 'components/tracking/performance/PerformanceCards';
import RankingSection from 'components/tracking/ranking/RankingSection';
import TrackingSummaryCards from 'components/tracking/summary/TrackingSummaryCards';
import TrackingBreadcrumb from 'components/tracking/TrackingBreadcrumb';
import TrendChart from 'components/tracking/trend/TrendChart';
import TrendHighlights from 'components/tracking/trend/TrendHighlights';
import { useGetTrackingList, useContentRankingExport } from 'hooks/queries/tracking';
import CoreGrayButton from 'shared/button/CoreGrayButton';
import styled from 'styled-components';

import CoreLoading from 'shared/CoreLoading';
import { ReportSection, ReportSectionHeader } from 'styles/styled-components/report';
import {
	Background,
	BodyInnerWrapper,
	BodyWrapper,
} from 'styles/styled-components/section';
import style from 'styles/styled-components/style';

const TrackingDetail = () => {
	const { code } = useParams<{ code: string }>();
	const { data: trackingList, isLoading: isListLoading } = useGetTrackingList();
	const [trendParams, setTrendParams] = useState<ITrendParams | undefined>();
	const [selectedDate, setSelectedDate] = useState<string | null>(null);
	const { mutate: exportExcel, isPending: isExporting } = useContentRankingExport();

	// trendParams 변경 시 날짜 선택 초기화 (TrendChart 내부에서도 초기화하지만 명시적으로 동기화)
	useEffect(() => {
		setSelectedDate(null);
	}, [trendParams]);

	const currentTracking = trackingList?.find((item) => item.code === code);

	if (isListLoading) {
		return (
			<Background>
				<BodyWrapper
					options={{
						scrollbars: { autoHide: 'move', autoHideDelay: 250 },
					}}
				>
					<BodyInnerWrapper>
						<Grid flex="center" _height="400px">
							<CoreLoading color_type="black" size_type="large" />
						</Grid>
					</BodyInnerWrapper>
				</BodyWrapper>
			</Background>
		);
	}

	if (!code || !currentTracking) {
		return (
			<Background>
				<BodyWrapper
					options={{
						scrollbars: { autoHide: 'move', autoHideDelay: 250 },
					}}
				>
					<BodyInnerWrapper>
						<Grid flex="center" _height="400px">
							<Text typo="p" color="gray">
								트래킹 정보를 찾을 수 없습니다.
							</Text>
						</Grid>
					</BodyInnerWrapper>
				</BodyWrapper>
			</Background>
		);
	}

	return (
		<Background>
			<BodyWrapper
				options={{
					scrollbars: { autoHide: 'move', autoHideDelay: 250 },
				}}
			>
				<BodyInnerWrapper direction="column">
					{/* 브레드크럼 + 트래킹 전환 */}
					<TrackingBreadcrumb code={code} name={currentTracking.name} />

					<ReportSection>
						<Grid flex="start" direction="column">
							{/* 현황 */}
							<SectionBlock>
								<TrackingSummaryCards code={code} />
							</SectionBlock>

							<SectionDivider />

							{/* 콘텐츠 성과 요약 */}
							<SectionBlock>
								<ReportSectionHeader>콘텐츠 성과 요약</ReportSectionHeader>
								<ContentTypeDistribution code={code} />
								<Grid _margin="16px 0 0 0">
									<PerformanceCards code={code} />
								</Grid>
							</SectionBlock>

							<SectionDivider />

							{/* 콘텐츠 성과 추이 */}
							<SectionBlock>
								<TrendChart
									code={code}
									onFilterChange={setTrendParams}
									onBarClick={setSelectedDate}
									selectedDate={selectedDate}
									trackingStartDate={currentTracking.trackingStartDate}
									trackingEndDate={currentTracking.trackingEndDate}
								/>
								<TrendHighlights
									code={code}
									trendParams={trendParams}
									selectedDate={selectedDate}
									onClearDate={() => setSelectedDate(null)}
								/>
							</SectionBlock>

							<SectionDivider />

							{/* 랭킹 */}
							<SectionBlock>
								<Grid flex="between" align_items="center">
									<ReportSectionHeader>랭킹</ReportSectionHeader>
									<CoreGrayButton
										size="default"
										style_type="filled"
										text={isExporting ? '다운로드 중...' : '엑셀 다운로드'}
										_onClick={() => {
											if (!code || isExporting) return;
											exportExcel({ code, params: {} });
										}}
									/>
								</Grid>
								<Text typo="p" _margin="0 0 16px 0">
									발행된 콘텐츠와 크리에이터 랭킹을 한 눈에 볼 수 있습니다.
								</Text>
								<RankingSection code={code} trackingStartDate={currentTracking.trackingStartDate} trackingEndDate={currentTracking.trackingEndDate} />
							</SectionBlock>
						</Grid>
					</ReportSection>
				</BodyInnerWrapper>
			</BodyWrapper>
		</Background>
	);
};

const SectionBlock = styled.div`
	width: 100%;
`;

const SectionDivider = styled.hr`
	width: 100%;
	height: 1px;
	border: none;
	background-color: ${({ theme }) => theme.colors.gray.gray15};
	margin: 72px 0;
`;

const { Grid, Text } = style;

export default TrackingDetail;
